import localforage from 'localforage';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { encryptedStorage } from '../lib';

import type { RegisterConfiguration } from '../models';
import type { MappedConfigurationResponse } from '../types';

interface RegisterConfigurationStore {
    config: Partial<RegisterConfiguration>;
    update: (data: Partial<MappedConfigurationResponse>) => void;
    reset: VoidFunction;
}

const transform = (data: Partial<MappedConfigurationResponse>) => {
    const transformedData: Partial<MappedConfigurationResponse> = {
        ...data,
    };

    delete transformedData.taxConfiguration;
    delete transformedData.paymentMethodConfiguration;

    return transformedData as RegisterConfiguration;
};

const OBSCURED_STORAGE_KEY = '8f3a9c2e_v1';
const OLD_STORAGE_KEY = 'register-configuration';

export async function migrateRegisterConfigurationToEncryptedStorage() {
    const oldData = await localforage.getItem<string>(OLD_STORAGE_KEY);

    if (!oldData) {
        return;
    }

    const parsedData = JSON.parse(oldData);

    useRegisterConfiguration.setState(parsedData.state, true);

    await localforage.removeItem(OLD_STORAGE_KEY);
}

export async function rehydrateConfigStorage() {
    const decryptedValue = await encryptedStorage.getItem(OBSCURED_STORAGE_KEY);

    if (!decryptedValue) return;

    const parsedValue = JSON.parse(decryptedValue);

    useRegisterConfiguration.setState({ config: parsedValue.state.config });
}

export const useRegisterConfiguration = create(
    persist<RegisterConfigurationStore>(
        (set, get) => ({
            config: {},
            reset: () => set({ config: {} }),
            update: (data: Partial<MappedConfigurationResponse>) => {
                const updatedData = { ...get().config, ...data };
                set({ config: transform(updatedData) });
            },
        }),
        {
            name: OBSCURED_STORAGE_KEY,
            storage: createJSONStorage(() => encryptedStorage),
            migrate: (persistedState, _version) => {
                return persistedState as RegisterConfigurationStore;
            },
            skipHydration: true,
        },
    ),
);
